/**
 * 自适应音乐管理器
 * 优先使用 Web Audio API，不支持时自动降级到 HTML5 Audio
 */
import { AudioManager } from './audio-manager';
import type { PlayOptions, AudioConfigMap } from './types';
import { MusicManagerWebAudio } from './music-manager-web-audio';
import { MusicManager } from './music-manager';
import { isAudioContextSupported } from './audio-utils';

/**
 * 自适应音乐管理器
 * 自动选择最佳实现（Web Audio API 或 HTML5 Audio）
 */
export class MusicManagerAdaptive {
  private implementation: MusicManagerWebAudio | MusicManager;
  private isWebAudio: boolean;

  constructor(audioManager: AudioManager) {
    this.isWebAudio = isAudioContextSupported();

    if (this.isWebAudio) {
      console.log('[MusicManager] Using Web Audio API implementation');
      this.implementation = new MusicManagerWebAudio(audioManager);
    } else {
      console.warn('[MusicManager] AudioContext not supported, falling back to HTML5 Audio');
      this.implementation = new MusicManager(audioManager);
    }
  }

  /**
   * 获取当前使用的实现类型
   */
  getImplementationType(): 'web-audio' | 'html5-audio' {
    return this.isWebAudio ? 'web-audio' : 'html5-audio';
  }

  /**
   * 是否使用 Web Audio API
   */
  isUsingWebAudio(): boolean {
    return this.isWebAudio;
  }

  // ==================== 以下方法代理到具体实现 ====================

  register(id: string, path: string): void {
    this.implementation.register(id, path);
  }

  registerAll(configMap: AudioConfigMap): void {
    this.implementation.registerAll(configMap);
  }

  isRegistered(id: string): boolean {
    return this.implementation.isRegistered(id);
  }

  unregister(id: string): void {
    this.implementation.unregister(id);
  }

  async preload(id: string): Promise<void> {
    return this.implementation.preload(id);
  }

  async playMusic(id: string, options?: PlayOptions): Promise<void> {
    return this.implementation.playMusic(id, options);
  }

  stopMusic(): void {
    this.implementation.stopMusic();
  }

  pauseMusic(): void {
    this.implementation.pauseMusic();
  }

  resumeMusic(): void {
    this.implementation.resumeMusic();
  }

  async fadeInMusic(id: string, duration?: number): Promise<void> {
    return this.implementation.fadeInMusic(id, duration);
  }

  fadeOutMusic(duration?: number, callback?: () => void): void {
    this.implementation.fadeOutMusic(duration, callback);
  }

  updateVolume(): void {
    this.implementation.updateVolume();
  }

  /**
   * 解锁音频上下文（仅 Web Audio 实现支持）
   * HTML5 Audio 实现会返回 true（不需要解锁）
   */
  async unlockAudioContext(): Promise<boolean> {
    if (this.isWebAudio && 'unlockAudioContext' in this.implementation) {
      return (this.implementation as MusicManagerWebAudio).unlockAudioContext();
    }
    return true;
  }

  /**
   * 销毁管理器（仅 Web Audio 实现支持）
   */
  destroy(): void {
    if ('destroy' in this.implementation) {
      (this.implementation as MusicManagerWebAudio).destroy();
    }
  }
}
